import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Mail, ArrowLeft, KeyRound } from 'lucide-react'
import { Button } from '@/components/ui/button'
import api from '@/api/client'
import { toast } from '@/components/ui/toaster'

export default function ForgotPassword() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      await api.post('/auth/forgot-password', { email })
      setSent(true)
      toast({ title: t('auth.resetLinkSent') })
    } catch (err: any) {
      toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center mx-auto mb-4">
            <KeyRound className="w-6 h-6 text-white" />
          </div>
          <h1 className="font-display text-2xl font-bold">{t('auth.forgotPassword')}</h1>
          <p className="text-muted-foreground mt-2">{t('auth.forgotSubtitle')}</p>
        </div>

        {sent ? (
          <div className="p-6 rounded-2xl bg-card border border-border text-center space-y-4">
            <Mail className="w-10 h-10 text-primary mx-auto" />
            <p className="text-sm text-muted-foreground">{t('auth.checkEmail')}</p>
            <Link to="/login" className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
              <ArrowLeft className="w-4 h-4" /> {t('auth.backToLogin')}
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">{t('auth.email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder={t('auth.emailPlaceholder')}
              />
            </div>
            <Button type="submit" className="w-full rounded-lg" disabled={loading}>
              <Mail className="w-4 h-4 mr-2" />
              {loading ? t('common.sending') : t('auth.sendResetLink')}
            </Button>
          </form>
        )}

        <p className="text-center mt-6 text-sm text-muted-foreground">
          <Link to="/login" className="text-primary hover:underline inline-flex items-center gap-1">
            <ArrowLeft className="w-3.5 h-3.5" /> {t('auth.backToLogin')}
          </Link>
        </p>
      </motion.div>
    </div>
  )
}
